import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  Image, 
  Volume2, 
  Shuffle, 
  Brain, 
  Trophy,
  BookOpen,
  CheckCircle,
  Clock,
  GraduationCap,
  Award,
  LogOut,
  Key,
  Copy,
  Check,
  X
} from 'lucide-react'
import { useGame } from '../context/GameContext'
import { useGameSession, formatTime } from '../context/GameSessionContext'
import { unlockAudio } from '../utils/sounds'
import { categories } from '../data/categories'
import { Category } from '../data/types'
import { saveProgress, loginPlayer, loginPlayerRemote, createPlayerRemote, saveProgressRemote } from '../utils/api'
import NameEditor from '../components/NameEditor'
import { checkNewAchievements, PlayerStats } from '../data/achievements'
import { vocabulary, getWordsByCategories } from '../data/vocabulary'
import { Crown } from 'lucide-react'
import { saveMasterSession, MasterModeSaveRequest } from '../utils/api'
import { explainScoring } from '../utils/scoring'

function buildCategoryWordCounts(wordIds: number[]): Record<string, number> {
  const counts: Record<string, number> = {}
  const wordMap = new Map(vocabulary.map(v => [v.id, v.category]))
  wordIds.forEach(id => {
    const cat = wordMap.get(id)
    if (cat) counts[cat] = (counts[cat] || 0) + 1
  })
  return counts
}

export default function Home() {
  const { progress } = useGame()
  const navigate = useNavigate()
  const { 
    session, 
    endSession, 
    startSession,
    resetSession,
    getMultiplier
  } = useGameSession()

  const masterAutoSaveTriggeredRef = useRef(false)
  
  const [selectedCategories, setSelectedCategories] = useState<Category[]>(() => {
    const stored = sessionStorage.getItem('selectedCategories')
    return stored ? JSON.parse(stored) : []
  })
  const [showSummary, setShowSummary] = useState(false)
  const [summaryData, setSummaryData] = useState<{
    score: number; time: number; words: number; correct: number; total: number;
    multiplier: number; categories: string[]; achievements: string[]; password: string;
  } | null>(null)
  const [copied, setCopied] = useState(false)
  const [saving, setSaving] = useState(false)
  const [masterMode, setMasterMode] = useState(false)
  const [showMasterInfo, setShowMasterInfo] = useState(false)
  const [playerName, setPlayerName] = useState(() => localStorage.getItem('hsk2-player-name') || 'Player')
  const [savedTotalScore, setSavedTotalScore] = useState(0)
  const currentPassword = localStorage.getItem('hsk2-current-player') || ''

  useEffect(() => {
    if (!currentPassword) return
    loginPlayer(currentPassword)
      .then(player => setSavedTotalScore(player.totalScore || 0))
      .catch(() => setSavedTotalScore(0))
  }, [currentPassword])

  // Redirect to welcome if not logged in
  useEffect(() => {
    const currentPw = localStorage.getItem('hsk2-current-player')
    if (!currentPw) {
      navigate('/welcome')
      return
    }

    loginPlayer(currentPw)
      .then((data) => {
        setSavedTotalScore(data.totalScore || 0)
        if (data.name) setPlayerName(data.name)
      })
      .catch(() => {
        setSavedTotalScore(0)
      })
  }, [navigate])
  
  // Sync local masterMode state with session.isMasterMode (important for page refresh)
  // Reset to normal mode if session is not playing but was in master mode (e.g., after returning from leaderboard)
  useEffect(() => {
    if (session.isMasterMode && !session.isPlaying) {
      // User returned from leaderboard or similar - reset to normal mode
      resetSession()
      setMasterMode(false)
    } else {
      setMasterMode(session.isMasterMode)
    }
  }, [session.isMasterMode, session.isPlaying, resetSession])
  
  useEffect(() => {
    sessionStorage.setItem('selectedCategories', JSON.stringify(selectedCategories))
  }, [selectedCategories])

  const gameModes = [
    {
      id: 'picture-word',
      title: 'Picture → Word',
      titleCn: '看图选字',
      icon: Image,
      color: 'from-pink-500 to-rose-500',
      description: 'Look at the picture and choose the correct word',
    },
    {
      id: 'audio-word',
      title: 'Audio → Word',
      titleCn: '听音选字',
      icon: Volume2,
      color: 'from-blue-500 to-cyan-500',
      description: 'Listen and choose the correct word',
    },
    {
      id: 'quiz',
      title: 'Speed Quiz',
      titleCn: '快速答题',
      icon: Brain,
      color: 'from-purple-500 to-violet-500',
      description: 'Answer as fast as you can',
    },
    {
      id: 'matching',
      title: 'Matching',
      titleCn: '配对游戏',
      icon: Shuffle,
      color: 'from-green-500 to-emerald-500',
      description: 'Match Chinese words with English meanings',
    },
  ]

  const toggleCategory = (categoryId: Category) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    )
  }

  const selectAll = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([])
    } else {
      setSelectedCategories(categories.map(c => c.id))
    }
  }

  const selectRandom = () => {
    const shuffled = [...categories].sort(() => Math.random() - 0.5)
    const randomCategories = shuffled.slice(0, 3).map(c => c.id)
    setSelectedCategories(randomCategories)
  }

  const masterWordCount = getWordsByCategories(selectedCategories).length

  const handleGameClick = (gameId: string) => {
    if (selectedCategories.length === 0) {
      alert('Please select at least one category!')
      return
    }
    unlockAudio()
    if (masterMode && !session.isPlaying) {
      // In master mode, show info first
      setShowMasterInfo(true)
      return
    }
    if (!session.isPlaying) {
      startSession(selectedCategories)
    }
    // In master mode during active session, check if game already completed
    if (session.isMasterMode && session.masterGamesCompleted.includes(gameId)) {
      return
    }
    // Use navigate instead of window.location.href to preserve React context
    navigate(`/game/${gameId}`)
  }

  const handleMasterToggle = () => {
    if (selectedCategories.length === 0) {
      alert('Please select at least one category first!')
      return
    }
    if (session.isPlaying && session.isMasterMode) return // Locked in master mode
    setShowMasterInfo(true)
  }

  const handleMasterStart = () => {
    setShowMasterInfo(false)
    setMasterMode(true)
    unlockAudio()
    const totalWords = getWordsByCategories(selectedCategories).length
    startSession(selectedCategories, true, totalWords)
    sessionStorage.setItem('selectedCategories', JSON.stringify(selectedCategories))
  }

  const handleMasterCancel = () => {
    setShowMasterInfo(false)
    setMasterMode(false)
  }

  const handleExitMasterMode = async () => {
    if (!session.isMasterMode) return
    // Save master mode score and exit
    await handleSaveAndQuit()
  }

  const handleLearnClick = () => {
    if (selectedCategories.length === 0) {
      alert('Please select at least one category!')
      return
    }
    if (!session.isPlaying) {
      startSession(selectedCategories)
    }
    navigate('/learn')
  }

  const handleSaveAndQuit = useCallback(async () => {
    setSaving(true)
    const pw = localStorage.getItem('hsk2-current-player')
    if (!pw) {
      setSaving(false)
      navigate('/welcome')
      return
    }

    // Capture session data before ending
    const capturedSession = { ...session }
    let activePw = pw

    try {
      // Check if player exists in the remote DB
      let playerData = await loginPlayerRemote(pw)

      if (!playerData) {
        // Player only exists locally - migrate to DB
        const localData = await loginPlayer(pw) // gets local data
        const newPlayer = await createPlayerRemote(playerName || localData.name)
        if (newPlayer) {
          activePw = newPlayer.password
          // Save accumulated local stats as a migration session
          const localWords = localData.wordsLearned.map(w => ({ wordId: w.wordId, correct: true }))
          const migrated = await saveProgressRemote({
            password: activePw,
            name: playerName || localData.name,
            sessionScore: localData.totalScore,
            sessionCorrect: localData.totalCorrect,
            sessionTotal: localData.totalQuestions,
            sessionDuration: 0,
            gameMode: 'migration',
            categories: localData.categoriesPlayed,
            wordsPlayed: localWords,
            bestStreak: localData.bestStreak,
            isPerfect: false,
            achievements: localData.achievements,
          })
          playerData = migrated || newPlayer
          // Update localStorage with new DB password
          localStorage.setItem('hsk2-current-player', activePw)
        } else {
          // DB unreachable, fall back to local
          playerData = localData
        }
      }

      const wordCategoryMap = new Map(vocabulary.map(v => [v.id, v.category]))
      const wordsPlayed = Object.entries(progress.wordStats || {}).flatMap(([id, stat]) => {
        const wordId = Number(id)
        const categoryId = wordCategoryMap.get(wordId)
        const correctItems = Array.from({ length: stat.correct || 0 }, () => ({ wordId, correct: true, categoryId }))
        const wrongItems = Array.from({ length: stat.wrong || 0 }, () => ({ wordId, correct: false, categoryId }))
        return [...correctItems, ...wrongItems]
      })
      const existingWordIds = playerData.wordsLearned.map(w => w.wordId)
      const allWordIds = [...new Set([...existingWordIds, ...progress.wordsLearned])]
      const categoryWordsLearned = buildCategoryWordCounts(allWordIds)

      const stats: PlayerStats = {
        totalScore: playerData.totalScore + capturedSession.totalScore,
        bestStreak: Math.max(playerData.bestStreak, progress.maxStreak),
        wordsLearned: allWordIds.length,
        gamesPlayed: playerData.sessionsPlayed + 1,
        categoriesPlayed: [...new Set([...playerData.categoriesPlayed, ...capturedSession.selectedCategories])].length,
        perfectGames: playerData.perfectGames + (capturedSession.totalQuestions > 0 && capturedSession.correctAnswers === capturedSession.totalQuestions ? 1 : 0),
        totalCorrect: playerData.totalCorrect + capturedSession.correctAnswers,
        totalQuestions: playerData.totalQuestions + capturedSession.totalQuestions,
        categoryWordsLearned,
      }

      const newlyUnlocked = checkNewAchievements(stats, playerData.achievements)
      const now = new Date().toISOString()
      const newAchievementEntries = newlyUnlocked.map(a => ({ id: a.id, unlockedAt: now }))

      const categoryStats = capturedSession.selectedCategories.map(categoryId => {
        const categoryWordIds = vocabulary.filter(v => v.category === categoryId).map(v => v.id)
        const uniqueSeen = categoryWordIds.filter(id => (progress.wordStats[id]?.correct || 0) + (progress.wordStats[id]?.wrong || 0) > 0).length
        const attempts = categoryWordIds.reduce((sum, id) => sum + (progress.wordStats[id]?.correct || 0) + (progress.wordStats[id]?.wrong || 0), 0)
        const correct = categoryWordIds.reduce((sum, id) => sum + (progress.wordStats[id]?.correct || 0), 0)
        const wrong = categoryWordIds.reduce((sum, id) => sum + (progress.wordStats[id]?.wrong || 0), 0)
        return { categoryId, uniqueSeen, attempts, correct, wrong }
      })

      const updatedPlayer = await saveProgress({
        password: activePw,
        name: playerName,
        sessionScore: capturedSession.totalScore,
        sessionCorrect: capturedSession.correctAnswers,
        sessionTotal: capturedSession.totalQuestions,
        sessionDuration: capturedSession.elapsedTime,
        gameMode: 'mixed',
        categories: capturedSession.selectedCategories,
        wordsPlayed,
        categoryStats,
        bestStreak: progress.maxStreak,
        isPerfect: capturedSession.totalQuestions > 0 && capturedSession.correctAnswers === capturedSession.totalQuestions,
        achievements: newAchievementEntries,
      })

      localStorage.setItem('hsk2-current-player', updatedPlayer.password)

      setSummaryData({
        score: capturedSession.totalScore,
        time: capturedSession.elapsedTime,
        words: capturedSession.wordsLearned,
        correct: capturedSession.correctAnswers,
        total: capturedSession.totalQuestions,
        multiplier: capturedSession.multiplier,
        categories: capturedSession.selectedCategories,
        achievements: newlyUnlocked.map(a => `${a.icon} ${a.title}`),
        password: updatedPlayer.password,
      })
    } catch {
      // Fallback: still show summary even if save fails
      setSummaryData({
        score: capturedSession.totalScore,
        time: capturedSession.elapsedTime,
        words: capturedSession.wordsLearned,
        correct: capturedSession.correctAnswers,
        total: capturedSession.totalQuestions,
        multiplier: capturedSession.multiplier,
        categories: capturedSession.selectedCategories,
        achievements: [],
        password: pw || '',
      })
    } finally {
      // Save master mode entry if applicable
      if (capturedSession.isMasterMode) {
        const masterSessionId = capturedSession.masterSessionId || ''
        const savedKey = 'hsk2-master-saved-session-ids'
        const savedIds: string[] = (() => {
          try {
            return JSON.parse(localStorage.getItem(savedKey) || '[]')
          } catch {
            return []
          }
        })()

        const alreadySaved = masterSessionId && savedIds.includes(masterSessionId)
        const statValues = Object.values(capturedSession.masterGameStats)
        const totalCorrect = statValues.reduce((s, g) => s + g.correct, 0)
        const totalAttempts = statValues.reduce((s, g) => s + g.total, 0)
        // Sum time from all individual game stats to get cumulative time
        const cumulativeTime = statValues.reduce((s, g) => s + g.time, 0)
        const masterReq: MasterModeSaveRequest = {
          password: activePw || '',
          categories: capturedSession.selectedCategories,
          totalWords: capturedSession.masterTotalWords,
          gamesCompleted: capturedSession.masterGamesCompleted.length,
          isComplete: capturedSession.masterGamesCompleted.length >= 4,
          totalScore: capturedSession.totalScore,
          totalTime: cumulativeTime,
          overallAccuracy: totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0,
          maxStreak: Math.max(0, ...statValues.map(g => g.maxStreak)),
          gameStats: capturedSession.masterGameStats,
        }

        if (!alreadySaved) {
          let saveSucceeded = false
          try {
            await saveMasterSession(masterReq)
            saveSucceeded = true
          } catch {
            // ignore - saveMasterSession already falls back to local
          }

          if (saveSucceeded && masterSessionId) {
            const next = [masterSessionId, ...savedIds].slice(0, 50)
            localStorage.setItem(savedKey, JSON.stringify(next))
          }

          try {
            sessionStorage.setItem('master-highlight', JSON.stringify({
              playerId: activePw || '',
              totalScore: capturedSession.totalScore,
              date: new Date().toISOString(),
              isComplete: capturedSession.masterGamesCompleted.length >= 4,
            }))
          } catch {
            // ignore
          }
        }
      }
      setSaving(false)
      endSession()
      resetSession()
      setShowSummary(true)
    }
  }, [endSession, navigate, playerName, progress, resetSession, session])

  const triggerMasterAutoSave = useCallback(() => {
    if (masterAutoSaveTriggeredRef.current) return
    masterAutoSaveTriggeredRef.current = true
    handleSaveAndQuit().then(() => {
      navigate('/leaderboard?tab=master')
    })
  }, [handleSaveAndQuit, navigate])

  useEffect(() => {
    const completed = sessionStorage.getItem('masterModeCompleted')
    if (completed === 'true') {
      sessionStorage.removeItem('masterModeCompleted')
      if (session.isMasterMode) {
        triggerMasterAutoSave()
      }
    }
  }, [session.isMasterMode, triggerMasterAutoSave])

  useEffect(() => {
    if (session.isMasterMode && session.masterGamesCompleted.length >= 4) {
      triggerMasterAutoSave()
    }
  }, [session.isMasterMode, session.masterGamesCompleted.length, triggerMasterAutoSave])

  const copyPassword = () => {
    if (summaryData?.password) {
      navigator.clipboard.writeText(summaryData.password).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
    }
  }

  const currentMultiplier = session.isPlaying ? session.multiplier : getMultiplier(selectedCategories.length)
  const accuracy = session.totalQuestions > 0 ? Math.round((session.correctAnswers / session.totalQuestions) * 100) : 0

  return (
    <div className={`page-shell p-3 md:p-4 flex flex-col ${
      session.isMasterMode || masterMode
        ? 'bg-gradient-to-br from-red-600 via-rose-600 to-orange-600'
        : 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500'
    }`}>
      <div className="max-w-6xl mx-auto w-full flex-1 flex flex-col">
        {/* Header */}
        <div className="mb-3">
          {/* Top layout */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-3">
            <div className="min-w-0 text-center md:text-left">
              <h1 className="text-xl md:text-2xl font-bold text-white drop-shadow-lg leading-tight">HSK2 Game</h1>
              <div className="text-white/80 text-xs flex items-center justify-center md:justify-start gap-1">
                <span>👋</span>
                <NameEditor
                  currentName={playerName}
                  password={currentPassword}
                  onRenamed={(newName) => setPlayerName(newName)}
                  textClassName="text-white/80 text-xs font-medium"
                />
              </div>
            </div>

            {/* Score Card */}
            <div className="bg-white/20 backdrop-blur-lg rounded-xl px-2 py-1.5 md:px-4 md:py-2 flex items-center justify-center gap-1.5 md:gap-4 min-w-0 self-center md:self-auto">
              <div className="text-center">
                <div className="text-lg md:text-xl font-bold text-white">{savedTotalScore}</div>
                <div className="text-white/60 text-[10px]" title="Saved cumulative score shown on leaderboard">Leaderboard</div>
              </div>
              <div className="border-l border-white/30 pl-2 md:pl-4 text-center">
                <div className="text-lg md:text-xl font-bold text-cyan-300">+{session.totalScore}</div>
                <div className="text-white/60 text-[10px]" title={explainScoring()}>Session</div>
              </div>
              <div className="border-l border-white/30 pl-2 md:pl-4 text-center">
                <div className="text-lg md:text-xl font-bold text-yellow-300">{savedTotalScore + session.totalScore}</div>
                <div className="text-white/60 text-[10px]">Preview Total</div>
              </div>
              {session.isPlaying ? (
                <>
                  <div className="border-l border-white/30 pl-2 md:pl-4 text-center">
                    <div className="text-lg md:text-xl font-bold text-yellow-300">{session.multiplier.toFixed(1)}x</div>
                    <div className="text-white/60 text-[10px]">Mult</div>
                  </div>
                  <div className="border-l border-white/30 pl-2 md:pl-4 text-center">
                    <div className="text-lg md:text-xl font-bold text-green-300">{formatTime(session.elapsedTime)}</div>
                    <div className="text-white/60 text-[10px]">Time</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="border-l border-white/30 pl-2 md:pl-4 text-center">
                    <div className="text-lg md:text-xl font-bold text-yellow-300">🔥 {progress.streak}</div>
                    <div className="text-white/60 text-[10px]">Streak</div>
                  </div>
                  <div className="border-l border-white/30 pl-2 md:pl-4 text-center">
                    <div className="text-lg md:text-xl font-bold text-green-300">{progress.wordsLearned.length}</div>
                    <div className="text-white/60 text-[10px]">Words</div>
                  </div>
                </>
              )}
            </div>

          </div>

          {/* Nav buttons */}
          <div className="grid grid-cols-4 gap-2 mt-2">
            <Link to="/progress" className="bg-white/15 hover:bg-white/25 rounded-xl px-2 py-2.5 md:py-2 active:scale-95 transition-all flex items-center justify-center gap-1.5" title="Progress">
              <Trophy className="w-5 h-5 text-yellow-300" />
              <span className="text-[11px] md:text-xs font-semibold text-white">Progress</span>
            </Link>
            <Link to="/leaderboard" className="bg-white/15 hover:bg-white/25 rounded-xl px-2 py-2.5 md:py-2 active:scale-95 transition-all flex items-center justify-center gap-1.5" title="Leaderboard">
              <Award className="w-5 h-5 text-amber-300" />
              <span className="text-[11px] md:text-xs font-semibold text-white">Rank</span>
            </Link>
            <Link to="/achievements" className="bg-white/15 hover:bg-white/25 rounded-xl px-2 py-2.5 md:py-2 active:scale-95 transition-all flex items-center justify-center gap-1.5" title="Achievements">
              <GraduationCap className="w-5 h-5 text-purple-300" />
              <span className="text-[11px] md:text-xs font-semibold text-white">Awards</span>
            </Link>
            <button
              onClick={handleSaveAndQuit}
              disabled={saving}
              className="bg-red-500/60 hover:bg-red-500/80 rounded-xl px-2 py-2.5 md:py-2 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
              title="Save & Quit"
            >
              <LogOut className="w-5 h-5 text-white" />
              <span className="text-[11px] md:text-xs font-semibold text-white">Exit</span>
            </button>
          </div>
        </div>

        {/* Categories Selection - always visible */}
        <div className={`bg-white/20 backdrop-blur-lg rounded-2xl p-3 mb-3 shadow-xl ${session.isPlaying && session.isMasterMode ? 'opacity-60' : ''}`}>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-white" />
              <h2 className="text-sm font-bold text-white">Categories</h2>
              <span className="text-white/70 text-xs">({selectedCategories.length})</span>
              <span className="bg-yellow-400 rounded-lg px-1.5 py-0.5 text-yellow-900 text-xs font-bold">
                {currentMultiplier.toFixed(1)}x
              </span>
              {session.isPlaying && session.isMasterMode && (
                <span className="text-yellow-200 text-xs font-bold">🔒 Locked in Master Mode</span>
              )}
            </div>
            <div className="flex gap-1.5">
              <button
                onClick={selectAll}
                disabled={session.isPlaying && session.isMasterMode}
                className="bg-white/20 text-white rounded-lg px-2 py-0.5 text-xs font-medium
                           active:scale-95 transition-transform flex items-center gap-1
                           disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CheckCircle className="w-3 h-3" />
                {selectedCategories.length === categories.length ? 'None' : 'All'}
              </button>
              <button
                onClick={selectRandom}
                disabled={session.isPlaying && session.isMasterMode}
                className="bg-white/20 text-white rounded-lg px-2 py-0.5 text-xs font-medium
                           active:scale-95 transition-transform flex items-center gap-1
                           disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Shuffle className="w-3 h-3" />
                Random
              </button>
            </div>
          </div>
          <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-14 gap-2.5 md:gap-3">
            {categories.map((cat) => {
              const isSelected = selectedCategories.includes(cat.id)
              return (
                <button
                  key={cat.id}
                  onClick={() => toggleCategory(cat.id)}
                  disabled={session.isPlaying && session.isMasterMode}
                  className={`${cat.color} rounded-lg p-1 text-center shadow
                             transition-all duration-150 ${isSelected ? 'ring-2 ring-yellow-300 ring-offset-1 ring-offset-transparent' : 'opacity-40 grayscale-[30%]'}
                             ${(session.isPlaying && session.isMasterMode) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="text-sm leading-none">{cat.icon}</div>
                  <div className="text-[8px] text-white font-medium leading-tight mt-0.5">{cat.nameEn}</div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Session info bar (when playing) */}
        {session.isPlaying && (
          <div className="bg-white/10 rounded-xl px-3 py-1.5 mb-2 flex items-center justify-between">
            <div className="flex items-center gap-3 text-white/80 text-xs">
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatTime(session.elapsedTime)}</span>
              <span>🎯 {accuracy}%</span>
              <span>{session.wordsLearned} words</span>
            </div>
          </div>
        )}

        {/* Mode Toggle */}
        <div className="bg-white/10 backdrop-blur rounded-2xl p-2 mb-3 flex items-center gap-3">
          <button
            onClick={() => { if (!session.isPlaying) setMasterMode(false) }}
            disabled={session.isPlaying && session.isMasterMode || selectedCategories.length === 0}
            className={`flex-1 rounded-xl py-2 px-3 text-sm font-bold transition-all flex items-center justify-center gap-1.5
              ${!masterMode && !session.isMasterMode ? 'bg-white text-purple-700 shadow-lg' : 'text-white/60'}
              ${(session.isPlaying && session.isMasterMode) || selectedCategories.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            🎮 Normal Mode
          </button>
          <button
            onClick={handleMasterToggle}
            disabled={session.isPlaying && session.isMasterMode || selectedCategories.length === 0}
            className={`flex-1 rounded-xl py-2 px-3 text-sm font-bold transition-all flex items-center justify-center gap-1.5
              ${masterMode || session.isMasterMode ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-900 shadow-lg' : 'text-white/60'}
              ${(session.isPlaying && session.isMasterMode) || selectedCategories.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Crown className="w-4 h-4" /> Master Mode
          </button>
        </div>

        {/* Master Mode Game Progress (when active) */}
        {session.isPlaying && session.isMasterMode && (
          <div className="bg-gradient-to-r from-yellow-400/20 to-amber-500/20 border border-yellow-400/30 rounded-2xl p-3 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4 text-yellow-300" />
              <span className="text-yellow-200 font-bold text-sm">Master Mode — {session.masterGamesCompleted.length}/4 Games</span>
              <span className="text-yellow-300/60 text-xs ml-auto">{session.masterTotalWords} words</span>
            </div>
            <div className="flex gap-2 mb-2">
              {gameModes.map(m => {
                const done = session.masterGamesCompleted.includes(m.id)
                return (
                  <div key={m.id} className={`flex-1 rounded-lg py-1 text-center text-xs font-bold
                    ${done ? 'bg-green-500/40 text-green-200' : 'bg-white/10 text-white/50'}`}>
                    {done ? '✅' : '⬜'} {m.title}
                  </div>
                )
              })}
            </div>
            <button
              onClick={handleExitMasterMode}
              disabled={saving}
              className={`w-full bg-red-500 text-white rounded-xl py-2 font-bold text-sm flex items-center justify-center gap-2
                ${saving ? 'opacity-50 cursor-not-allowed' : 'active:scale-95 transition-transform'}`}
            >
              {saving ? 'Saving...' : '🚪 End & Exit Master Mode'}
            </button>
            {session.masterGamesCompleted.length === 4 && (
              <div className="mt-2 text-center text-yellow-200 font-bold text-sm animate-pulse">
                🏆 All games complete! Exit to record your Master Mode score!
              </div>
            )}
          </div>
        )}

        {/* Learn Mode - prominent */}
        <button
          onClick={handleLearnClick}
          className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl p-3 mb-3 shadow-xl
                     active:scale-95 transition-transform flex items-center gap-3"
        >
          <div className="w-12 h-12 bg-white/30 rounded-xl flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1 text-left">
            <h3 className="text-lg md:text-xl font-bold text-white">📖 Learn First!</h3>
            <p className="text-white/90 text-xs md:text-sm">先学习词汇 · Study vocabulary before playing</p>
          </div>
        </button>

        {/* Game Modes - 2x2 Grid */}
        <div className="flex-1">
          <div className="grid grid-cols-2 gap-2.5 md:gap-4">
            {gameModes.map((mode) => {
              const Icon = mode.icon
              const masterDone = session.isMasterMode && session.masterGamesCompleted.includes(mode.id)
              return (
                <button
                  key={mode.id}
                  onClick={() => handleGameClick(mode.id)}
                  disabled={masterDone}
                  className={`bg-gradient-to-r ${mode.color} rounded-2xl p-3 md:p-5 shadow-xl 
                             active:scale-95 transition-transform duration-150 flex flex-col relative
                             ${masterDone ? 'opacity-50 grayscale' : ''}`}
                >
                  {masterDone && (
                    <div className="absolute top-2 right-2 bg-green-500 rounded-full p-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div className="flex items-center gap-2 md:gap-4 mb-1">
                    <div className="w-9 h-9 md:w-12 md:h-12 bg-white/30 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <h3 className="text-base md:text-xl font-bold text-white leading-tight">{mode.title}</h3>
                      <p className="text-white/90 chinese-text text-xs md:text-base">{mode.titleCn}</p>
                    </div>
                  </div>
                  <p className="text-white/70 text-[10px] md:text-xs text-left">{mode.description}</p>
                </button>
              )
            })}
          </div>
        </div>

      </div>

      {/* Master Mode Info Modal */}
      {showMasterInfo && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-yellow-600 to-amber-700 rounded-3xl p-6 max-w-md w-full shadow-2xl">
            <div className="text-center mb-4">
              <div className="text-5xl mb-2">👑</div>
              <h2 className="text-2xl font-bold text-white">Master Mode</h2>
              <p className="text-white/70 text-sm mt-1">Challenge Mode</p>
            </div>
            <div className="bg-black/20 rounded-2xl p-4 mb-4 space-y-2">
              <p className="text-white text-sm">📌 You must answer <strong>all {masterWordCount} words</strong> correctly in each game</p>
              <p className="text-white text-sm">🎮 Complete all <strong>4 games</strong> to finish Master Mode</p>
              <p className="text-white text-sm">🏆 Higher score with more categories selected</p>
              <p className="text-white text-sm">🚪 You can quit anytime — partial scores are saved</p>
            </div>
            <div className="bg-black/20 rounded-2xl p-3 mb-4">
              <p className="text-white/70 text-xs mb-1">Selected categories ({selectedCategories.length})</p>
              <div className="flex flex-wrap gap-1">
                {selectedCategories.map(cat => {
                  const catInfo = categories.find(c => c.id === cat)
                  return catInfo ? (
                    <span key={cat} className="bg-white/20 rounded-lg px-2 py-0.5 text-white text-xs">
                      {catInfo.icon} {catInfo.nameEn}
                    </span>
                  ) : null
                })}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleMasterCancel}
                className="flex-1 bg-white/20 text-white rounded-2xl py-3 font-bold active:scale-95 transition-transform"
              >
                ❌ Cancel
              </button>
              <button
                onClick={handleMasterStart}
                className="flex-1 bg-white text-yellow-800 rounded-2xl py-3 font-bold active:scale-95 transition-transform"
              >
                🚀 Start
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save & Quit Summary Modal */}
      {showSummary && summaryData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-5 max-w-md w-full shadow-2xl max-h-[90vh] overflow-auto">
            {/* Close */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">Session Summary</h2>
              <button onClick={() => { setShowSummary(false); navigate('/welcome') }}
                className="bg-white/20 rounded-xl p-1.5 active:scale-95 transition-transform">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-white/15 rounded-2xl p-3 text-center">
                <p className="text-3xl font-bold text-yellow-300">{summaryData.score}</p>
                <p className="text-white/70 text-xs">Score ({summaryData.multiplier.toFixed(1)}x)</p>
              </div>
              <div className="bg-white/15 rounded-2xl p-3 text-center">
                <p className="text-3xl font-bold text-green-300">{formatTime(summaryData.time)}</p>
                <p className="text-white/70 text-xs">Time Played</p>
              </div>
              <div className="bg-white/15 rounded-2xl p-3 text-center">
                <p className="text-3xl font-bold text-blue-300">{summaryData.words}</p>
                <p className="text-white/70 text-xs">Words Learned</p>
              </div>
              <div className="bg-white/15 rounded-2xl p-3 text-center">
                <p className="text-3xl font-bold text-pink-300">
                  {summaryData.total > 0 ? Math.round((summaryData.correct / summaryData.total) * 100) : 0}%
                </p>
                <p className="text-white/70 text-xs">Accuracy ({summaryData.correct}/{summaryData.total})</p>
              </div>
            </div>

            {/* Categories played */}
            {summaryData.categories.length > 0 && (
              <div className="bg-white/10 rounded-2xl p-3 mb-3">
                <p className="text-white/70 text-xs mb-1">Categories played</p>
                <div className="flex flex-wrap gap-1">
                  {summaryData.categories.map(cat => {
                    const catInfo = categories.find(c => c.id === cat)
                    return catInfo ? (
                      <span key={cat} className="bg-white/20 rounded-lg px-2 py-0.5 text-white text-xs">
                        {catInfo.icon} {catInfo.nameEn}
                      </span>
                    ) : null
                  })}
                </div>
              </div>
            )}

            {/* Achievements */}
            {summaryData.achievements.length > 0 && (
              <div className="bg-yellow-400/20 rounded-2xl p-3 mb-3 border border-yellow-400/30">
                <p className="text-yellow-200 font-bold text-sm mb-1">🏆 New Achievements!</p>
                {summaryData.achievements.map((a, i) => (
                  <p key={i} className="text-white text-sm">{a}</p>
                ))}
              </div>
            )}

            {/* Password */}
            <div className="bg-black/30 rounded-2xl p-4 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <Key className="w-4 h-4 text-yellow-300" />
                <p className="text-white/80 text-sm font-bold">Your Password</p>
              </div>
              <p className="text-white/60 text-xs mb-2">
                Save this password to continue later
              </p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-mono font-bold text-yellow-300 tracking-widest flex-1">
                  {summaryData.password}
                </span>
                <button
                  onClick={copyPassword}
                  className="bg-white/20 rounded-xl px-3 py-2 active:scale-95 transition-transform flex items-center gap-1"
                >
                  {copied ? <Check className="w-4 h-4 text-green-300" /> : <Copy className="w-4 h-4 text-white" />}
                  <span className="text-white text-xs">{copied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
              <p className="text-white/40 text-[10px] mt-2">📸 Or take a screenshot to save it</p>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowSummary(false); navigate('/welcome') }}
                className="flex-1 bg-white/20 text-white rounded-2xl py-3 font-bold
                           active:scale-95 transition-transform"
              >
                Done
              </button>
              <Link
                to="/leaderboard"
                onClick={() => setShowSummary(false)}
                className="flex-1 bg-yellow-500 text-yellow-900 rounded-2xl py-3 font-bold text-center
                           active:scale-95 transition-transform"
              >
                Leaderboard
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
